import { useMemo, useState, useEffect } from "react";
import {
  Briefcase,
  Building2,
  Check,
  Phone,
  User,
  UserPlus,
  Wallet,
  X,
} from "lucide-react";
import Button from "../ui/Button";
import StatusBadge from "./StatusBadge";
import { formatCurrency } from "../../data/mock/dashboardData";

const TARGET_PRESETS = [10, 30, 50, 80, 100];
const FREQUENCIES = [
  ["weekly", "Weekly"],
  ["monthly", "Monthly"],
  ["yearly", "Yearly"],
  ["custom", "Custom dates"],
];

function windowLabel(frequency, from, to) {
  const start = new Date();
  const end = new Date(start);
  if (frequency === "monthly") end.setMonth(end.getMonth() + 1);
  else if (frequency === "yearly") end.setFullYear(end.getFullYear() + 1);
  else if (frequency === "custom") return from && to ? `${from} to ${to}` : "Choose both dates";
  else end.setDate(end.getDate() + 6);
  if (frequency === "monthly" || frequency === "yearly") end.setDate(end.getDate() - 1);
  return `${start.toISOString().slice(0, 10)} to ${end.toISOString().slice(0, 10)}`;
}

function getInitials(name) {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function CaseMetaItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-primary/[0.06] bg-white px-3 py-2.5">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/[0.06] text-primary/55">
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-primary/40">{label}</p>
        <p className="mt-0.5 truncate text-sm font-semibold text-primary">{value}</p>
      </div>
    </div>
  );
}

export default function AssignCollectorModal({
  caseRow,
  cases,
  collectors,
  selectedAgent,
  onSelectAgent,
  onAssign,
  onClose,
  assigning = false,
}) {
  useEffect(() => {
    if (!caseRow && !cases?.length) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [caseRow, cases, onClose]);

  const bulkCases = cases?.length ? cases : caseRow ? [caseRow] : [];
  const [preset, setPreset] = useState(50);
  const [customPercent, setCustomPercent] = useState("");
  const [frequency, setFrequency] = useState("weekly");
  const [targetFrom, setTargetFrom] = useState("");
  const [targetTo, setTargetTo] = useState("");
  const percent = preset === "custom" ? Number(customPercent) : Number(preset);
  const totalOutstanding = bulkCases.reduce(
    (sum, row) => sum + Number(row.outstandingAmount ?? row.outstanding ?? row.amountOwed ?? 0),
    0
  );
  const targetAmount = Number.isFinite(percent) ? (totalOutstanding * percent) / 100 : 0;
  const targetReady = percent >= 1 && percent <= 100 && (frequency !== "custom" || (targetFrom && targetTo && targetTo >= targetFrom));
  const targetPayload = useMemo(
    () => ({
      target_percent: percent,
      target_frequency: frequency,
      ...(frequency === "custom" ? { target_from: targetFrom, target_to: targetTo } : {}),
    }),
    [percent, frequency, targetFrom, targetTo]
  );
  if (!bulkCases.length) return null;

  const isBulk = bulkCases.length > 1;
  const primary = bulkCases[0];
  const selected = collectors.find(
    (agent) => agent.id === selectedAgent || agent.admin_slug === selectedAgent || agent.name === selectedAgent
  );
  const isReassign = bulkCases.every((row) => Boolean(row.assignedAgent));

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-labelledby="assign-collector-title"
        aria-modal="true"
        className="flex max-h-[min(92vh,680px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-[0_24px_64px_-12px_rgba(127,29,29,0.35)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-primary via-primary-light to-accent" aria-hidden />

        <div className="border-b border-primary/[0.06] bg-gradient-to-r from-primary/[0.03] to-accent/[0.05] px-5 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-primary/55 ring-1 ring-primary/10">
                  <UserPlus className="h-3 w-3 text-accent" />
                  {isBulk ? (isReassign ? "Bulk reassign" : "Bulk assign") : isReassign ? "Reassign case" : "Assign case"}
                </span>
                {!isBulk ? (
                  <span className="rounded-md bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-primary/45 ring-1 ring-primary/[0.06]">
                    {primary.id}
                  </span>
                ) : (
                  <span className="rounded-md bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-primary/45 ring-1 ring-primary/[0.06]">
                    {bulkCases.length} cases
                  </span>
                )}
              </div>
              <h2 id="assign-collector-title" className="text-lg font-bold text-primary">
                {isBulk
                  ? isReassign
                    ? "Reassign selected cases"
                    : "Assign filtered cases"
                  : isReassign
                    ? "Change agent"
                    : "Assign agent"}
              </h2>
              <p className="mt-1 text-sm text-primary/55">
                {isBulk
                  ? isReassign
                    ? "Move this quantity or range to one call centre agent. The current agent on each case is replaced."
                    : "Route every matching case to one call centre agent."
                  : "Route this case to an active call centre agent and balance workload across the team."}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-primary/45 transition-colors hover:bg-white hover:text-primary"
              aria-label="Close assign agent dialog"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-5 py-5 sm:px-6">
          <section>
            <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
              Case summary
            </p>
            <div className="rounded-xl border border-primary/[0.07] bg-primary/[0.02] p-4">
              {isBulk ? (
                <>
                  <p className="text-base font-bold text-primary">{bulkCases.length} cases selected</p>
                  <p className="mt-0.5 text-xs text-primary/45">
                    {primary.institution || primary.group_name || "Matching current filters"}
                  </p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <CaseMetaItem icon={Wallet} label="Total outstanding" value={formatCurrency(totalOutstanding)} />
                    <CaseMetaItem icon={Briefcase} label="Cases" value={String(bulkCases.length)} />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-base font-bold text-primary">{primary.customer}</p>
                      <p className="mt-0.5 text-xs text-primary/45">{primary.institution}</p>
                    </div>
                    <StatusBadge
                      status={primary.priority === "Urgent" ? "overdue" : "pending"}
                      label={primary.priority || primary.status || "Open"}
                    />
                  </div>

                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <CaseMetaItem icon={Wallet} label="Amount owed" value={formatCurrency(primary.amountOwed)} />
                    <CaseMetaItem
                      icon={Briefcase}
                      label="Days late"
                      value={`${primary.daysOverdue ?? 0} days`}
                    />
                    <CaseMetaItem icon={Building2} label="Institution" value={primary.institution || "—"} />
                    <CaseMetaItem
                      icon={User}
                      label="Current assignee"
                      value={primary.assignedAgent ?? "Unassigned"}
                    />
                  </div>
                </>
              )}
            </div>
          </section>

          <section className="mt-5">
            <div className="mb-3 flex items-end justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
                  Select agent
                </p>
                <p className="mt-0.5 text-xs text-primary/45">
                  Choose based on current caseload and availability
                </p>
              </div>
              <span className="shrink-0 rounded-lg bg-primary/[0.05] px-2 py-1 text-[10px] font-bold text-primary/50 ring-1 ring-primary/[0.06]">
                {collectors.length} available
              </span>
            </div>

            <ul className="space-y-2">
              {collectors.map((agent) => {
                const isSelected =
                  selectedAgent === agent.id ||
                  selectedAgent === agent.admin_slug ||
                  selectedAgent === agent.name;
                return (
                  <li key={agent.id}>
                    <button
                      type="button"
                      onClick={() => onSelectAgent(agent.id || agent.admin_slug || agent.name)}
                      className={`flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-all ${
                        isSelected
                          ? "border-primary/20 bg-gradient-to-r from-primary/[0.06] to-accent/[0.08] shadow-[0_2px_8px_rgba(127,29,29,0.08)] ring-1 ring-primary/10"
                          : "border-primary/[0.07] bg-white hover:border-primary/15 hover:bg-primary/[0.02]"
                      }`}
                    >
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xs font-bold ${
                          isSelected
                            ? "bg-primary text-white shadow-sm"
                            : "bg-primary/[0.08] text-primary"
                        }`}
                      >
                        {getInitials(agent.name)}
                      </span>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-sm font-bold text-primary">{agent.name}</p>
                          {isSelected && (
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white">
                              <Check className="h-3 w-3" strokeWidth={3} />
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-primary/45">{agent.role}</p>
                        <div className="mt-1.5 flex flex-wrap gap-3 text-[10px]">
                          <span>
                            <span className="font-bold text-primary">{agent.casesAssigned}</span>
                            <span className="text-primary/40"> cases</span>
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Phone className="h-3 w-3 text-primary/35" />
                            <span className="font-bold text-primary">{agent.contactsToday}</span>
                            <span className="text-primary/40"> contacts today</span>
                          </span>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="mt-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">Recovery target</p>
            <p className="mt-0.5 text-xs text-primary/45">
              Outstanding on this batch is {formatCurrency(totalOutstanding)}. The target is the share to recover in the window.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {TARGET_PRESETS.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setPreset(value)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-bold ring-1 ${
                    preset === value
                      ? "bg-primary text-white ring-primary"
                      : "bg-white text-primary ring-primary/15"
                  }`}
                >
                  {value}%
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPreset("custom")}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold ring-1 ${
                  preset === "custom" ? "bg-primary text-white ring-primary" : "bg-white text-primary ring-primary/15"
                }`}
              >
                Custom
              </button>
            </div>
            {preset === "custom" && (
              <label className="mt-3 block text-xs font-semibold text-primary/70">
                Custom percent
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={customPercent}
                  onChange={(event) => setCustomPercent(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-primary/15 px-3 py-2 text-sm"
                />
              </label>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {FREQUENCIES.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFrequency(value)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-bold ring-1 ${
                    frequency === value
                      ? "bg-primary text-white ring-primary"
                      : "bg-white text-primary ring-primary/15"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {frequency === "custom" && (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <label className="text-xs font-semibold text-primary/70">
                  From
                  <input type="date" value={targetFrom} onChange={(event) => setTargetFrom(event.target.value)} className="mt-1 w-full rounded-lg border border-primary/15 px-3 py-2 text-sm" />
                </label>
                <label className="text-xs font-semibold text-primary/70">
                  To
                  <input type="date" value={targetTo} onChange={(event) => setTargetTo(event.target.value)} className="mt-1 w-full rounded-lg border border-primary/15 px-3 py-2 text-sm" />
                </label>
              </div>
            )}
            <p className="mt-3 text-sm text-primary/80">
              Recover <span className="font-semibold text-primary">{formatCurrency(targetAmount)}</span> ({percent || 0}%) by {windowLabel(frequency, targetFrom, targetTo)}.
            </p>
          </section>

          {selected && (
            <section className="mt-4 flex items-start gap-3 rounded-xl border border-accent/25 bg-accent/[0.08] px-4 py-3.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
                <UserPlus className="h-4 w-4" />
              </span>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/50">
                  Assignment preview
                </p>
                <p className="mt-1 text-sm leading-relaxed text-primary/80">
                  <span className="font-semibold text-primary">
                    {isBulk ? `${bulkCases.length} cases` : primary.customer}
                  </span>{" "}
                  will be {isReassign ? "reassigned" : "assigned"}
                  to <span className="font-semibold text-primary">{selected.name}</span>
                  {selected.casesAssigned >= 20 ? (
                    <span className="text-amber-700"> — high caseload ({selected.casesAssigned} cases)</span>
                  ) : (
                    <span> — {selected.casesAssigned} cases currently assigned</span>
                  )}
                </p>
              </div>
            </section>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-primary/[0.06] bg-primary/[0.02] px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="accent"
            size="sm"
            onClick={() => onAssign(targetPayload)}
            disabled={!selectedAgent || !targetReady}
            loading={assigning}
          >
            {isBulk
              ? `${isReassign ? "Reassign" : "Assign"} ${bulkCases.length} cases`
              : isReassign
                ? "Confirm reassignment"
                : "Assign case"}
          </Button>
        </div>
      </div>
    </div>
  );
}
